import { Customer } from "@app/customer/model/customer";
import { ActionPayloadGeneric } from "@app/root-store/actions";
import { createActionSet, createCrudActionSets, createDeletePromptActionSet } from "@app/root-store/actions/action-creators";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { User } from "@app/user/model/user";
import { TypedAction } from "@ngrx/store/src/models";

export type CustomerAction = ActionPayload & TypedAction<string>;
export type CustomerSuccessAction = ActionResult & TypedAction<string>;
export type CustomerFailureAction = DataServiceError<ActionPayload> & TypedAction<string>;

export type CustomerActions = CustomerAction | CustomerSuccessAction | CustomerFailureAction;

/**
 * A payload used when performing an operation on a single entity
 */
export type ActionPayload = ActionPayloadGeneric & {
    customer: Customer;
};

/**
 * A payload used when loading an entity
 */
interface LoadActionPayload {
    customerId: number;
}

/**
 * An action result that returns a collection of entities
 */
export interface BulkActionResult {
    customers: ClareDbCollection<Customer>;
}

/**
 * An action result that returns a single entity
 */
export interface ActionResult {
    customer: Customer;
}

/**
 * @description Used in the action creators to generate an action type like:
 * "[Entity Management] Load Entity"
 */
const actionCategory = "Customer";

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
export const {
    load: loadSet,
    loadSingle: loadSingleSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { success: loadSuccess, failure: loadFailure } = loadSet;
export const { request: get, success: getSuccess, failure: getFailure } = loadSingleSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

/**
 * An action set to prompt a user when deleting an item
 */
export const { confirm: deleteConfirmation, dismiss: deleteConfirmationDismiss } =
    createDeletePromptActionSet<ActionPayload>(actionCategory);

export const deleteConfirmationSet = { ...deleteSet, deleteConfirmation, deleteConfirmationDismiss };

export const sendCustomerWelcomeEmailSet = createActionSet<{
    recipientEmail: string;
    recipientFirstName: string;
    recipientLastName: string;
    inviteCode: string;
}>(actionCategory, "Send Welcome Email");

export const { request: sendCustomerWelcomeEmail, success: sendCustomerWelcomeEmailSuccess } = sendCustomerWelcomeEmailSet;

export const sendCustomerModifiedLocationEmailSet = createActionSet<{
    removedUser: any;
    newUser: any;
    entityName: string;
}>(actionCategory, "Send Primary Contact Modified Email");

export const {
    request: sendCustomerModifiedLocationEmail,
    success: sendCustomerModifiedLocationEmailSuccess,
    failure: sendCustomerModifiedLocationEmailFailure,
} = sendCustomerModifiedLocationEmailSet;

export const sendCustomerMovedOutEmailSet = createActionSet<{
    removedUser: any;
    entityName: string;
}>(actionCategory, "Send Primary Contact Moved Out Email");

export const {
    request: sendCustomerMovedOutEmail,
    success: sendCustomerMovedOutEmailSuccess,
    failure: sendCustomerMovedOutEmailFailure,
} = sendCustomerMovedOutEmailSet;

export const {
    request: updateInviteCode,
    success: updateInviteCodeSuccess,
    failure: updateInviteCodeFailure,
} = createActionSet<ActionPayload>(actionCategory, "Update Invite Code");

export const sendCustomerMovedInEmailSet = createActionSet<{
    user: any;
    entityName: string;
    companyAdmins?: User[];
}>(actionCategory, "Send Primary Contact Moved In Email");

export const {
    request: sendCustomerMovedInEmail,
    success: sendCustomerMovedInEmailSuccess,
    failure: sendCustomerMovedInEmailFailure,
} = sendCustomerMovedInEmailSet;

export const sendRequestToChangeMonitoringSet = createActionSet<{
    user: any;
    companyName: string;
    address: any;
    isMoveIn: boolean;
    companyAdmins?: User[];
}>(actionCategory, "Send Request To Change Monitoring Service Status");

export const {
    request: sendRequestChangeMonitoring,
    success: sendRequestChangeMonitoringSuccess,
    failure: sendRequestChangeMonitoringFailure,
} = sendRequestToChangeMonitoringSet;
